import {
    useCallback, useMemo, useState,
} from 'react'
import {
    useJsApiLoader, Libraries,
} from '@react-google-maps/api'
import noop from 'lodash/noop'

import {
    Library,
} from 'app/types/googlemaps.types'
import FormWithControls from 'app/shared-components/FormWithControls'
import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import {
    defaultTaxCodes,
} from 'app/config'
import {
    OrderIncoTerm,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    RequestConfig,
} from 'app/types/request.types'

import {
    BillingCompany,
} from '../BillingCompanyForm.types'
import useStyles from './BillingCompanyForm.styles'
import getBillingFormFields from './BillingCompanyForm.fields'

type BillingFormProps = {
    data?: BillingCompany,
    onSuccess?: (...args: any[]) => void,
    onCancel?: (...args: any[]) => void,
    request?: RequestConfig,
    disabled?: boolean,
}

const defaultProps: Partial<BillingFormProps> = {
    data: {} as BillingCompany,
    request: undefined,
    disabled: false,
    onSuccess: noop,
    onCancel: noop,
}

const libraries: Libraries = [Library.PLACES]

const {
    REACT_APP_GOOGLE_MAP_KEY: googleMapKey,
} = process.env

const BillingCompanyForm = ({
    data,
    onSuccess,
    onCancel,
    request,
    disabled,
}: BillingFormProps) => {
    const getAllowedValues = useEnumValues()
    const {
        isLoaded: isGoogleMapsServiceLoaded,
    } = useJsApiLoader({
        googleMapsApiKey: googleMapKey,
        libraries,
    })

    const isNew = !data?.id

    const [
        value,
        setValue,
    ] = useState<BillingCompany>({
        ...data,
        defaultIncoTerm: isNew ? OrderIncoTerm.CPT : data?.defaultIncoTerm,
    })

    const {
        currentPlaceId,
    } = value

    const {
        classes,
    } = useStyles()

    const setAutocomplete = useCallback((val, isSetOwnField = false) => {
        const updatedValue = isSetOwnField
            ? {
                ...value,
                additional: val,
            }
            : {
                ...value,
                ...val,
            }

        if (JSON.stringify(updatedValue) !== JSON.stringify(value)) {
            setValue(updatedValue)
        }
    }, [value])

    const fields = useFieldsWithClassName(useMemo(() => {
        return getBillingFormFields({
            setAutocomplete,
            currentPlaceId,
            value,
            isGoogleMapsServiceLoaded,
            getAllowedValues,
            isNew,
            disabled,
        })
    }, [
        setAutocomplete,
        currentPlaceId,
        value,
        isGoogleMapsServiceLoaded,
        getAllowedValues,
        isNew,
        disabled,
    ]), classes)

    const onChangeValue = useCallback((val) => {
        const valueParsed = {
            ...val,
            hasBulkInvoice: val.hasBulkInvoice || false,
            defaultTaxCode: val.defaultTaxCode || defaultTaxCodes,
        }

        if (JSON.stringify(valueParsed) !== JSON.stringify(value)) {
            setValue(valueParsed)
        }
    }, [value])

    return (
        <FormWithControls
            name="BillingCompany"
            value={value}
            onSuccess={onSuccess}
            onCancel={onCancel}
            fields={fields}
            classNames={{
                gridWrapper: classes.gridWrapperBilling,
            }}
            setExternalValue={onChangeValue}
            disabled={disabled}
            requestParams={request}
        />
    )
}

BillingCompanyForm.defaultProps = defaultProps

export default BillingCompanyForm
